import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  selectGetCompanyPartnerRelations,
  selectGetCompanyPartnerRelationsState,
  selectUpdateMemberToPartnerState,
  selectUpdateCompanyPartnerState,
} from '../../../../store/slices/dashboardSlice';
import {
  getPartnerEnrollmentStatus,
  getHomeDepotEnrollmentStatus,
  homeDepotPartnerId,
  weSupplyTradesPartnerId,
} from '../../../../common/utils/PartnerEnrollmentUtils';
import { PartnerEnrollmentStatus } from '../../../../common/enums';
import { RenderEnrollmentBadge } from '../../../../common/RenderEnrollmentBadge';
import { PartnerEnrollmentStatusBadge } from '../../../../common/PartnerEnrollmentStatusBadge';
import { getPartnerEnrollmentStatusMessageKey } from '../../../../common/utils/PartnerEnrollmentStatusMessages';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import { usePartnerEnrollmentDialog } from './usePartnerEnrollmentDialog';
import { PartnerEnrollmentProvider } from '../Enrollment/usePartnerEnrollmentWithExtraStep';
import { useSolutionOrg } from '../../../../config/useSolutionOrg';
import {
  HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
  HOME_DEPOT_SETUP_WEB,
  PARTNERS_ROUTE_HOME_DEPOT,
} from '../../../../routes/namedRoutes';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import {
  selectMemberInformation,
  selectMemberSession,
} from '../../../../store/slices/onboardingSlice';
import {
  updateCompanyPartner,
  getCompanyPartnerRelations,
} from '../../../../store/thunks/dashboardThunk';
import type { UpdateCompanyPartnerRequest } from '../../../../api/dashboardAPI';
import OptimusButton from '../../../../common/library/Button/Button';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { usePartnerConfigList } from '../Enrollment/usePartnerConfigList';
import { HDEnrollmentStatusPanel } from '../../HDOnboardingFlow/HDEnrollmentStatusPanel';
import {
  HD_SITE_URL,
  getPartnerShopButtonBrandStyle,
} from '../../../../common/constants';
import { HDExistingAccModal } from '../../HDOnboardingFlow/HDExistingAccModal';
import { SubscriptionTier } from '../../../../common/models/Subscription';
import { TestIds } from '../../../../assets/testIds';
import { shouldAutoOpenHomeDepotEnrollModal } from '../../../../common/homeDepotEnrollmentIntent';

type PartnerOverviewSectionV2Props = {
  partnerId: string;
  overrideAction?: ReactNode;
  overrideEnrollmentStatus?: PartnerEnrollmentStatus;
  isEnrollmentActionLoading?: boolean;
};

export const PartnerOverviewSection = ({
  partnerId,
  overrideAction,
  overrideEnrollmentStatus,
  isEnrollmentActionLoading = false,
}: PartnerOverviewSectionV2Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isNativeAppExperience } = useSolutionOrg();
  const { openExternalLink } = useNavigationUtils();
  const companyPartnerRelations = useAppSelector(selectGetCompanyPartnerRelations);
  const updateMemberToPartnerState = useAppSelector(selectUpdateMemberToPartnerState);
  const updateCompanyPartnerState = useAppSelector(selectUpdateCompanyPartnerState);
  const getCompanyPartnerRelationsState = useAppSelector(selectGetCompanyPartnerRelationsState);
  const { memberId } = useAppSelector(selectMemberSession);
  const { company } = useAppSelector(selectMemberInformation);
  const { trackHomeDepotEnrollmentStart, trackPartnerEnrollment } = useCustomEventTracking();
  const {
    profileCategory,
    config,
    introduction,
  } = usePartnerProfileConfig();
  const {
    openPartnerModal,
    PartnerDialog,
    updatePartnerOpenState,
  } = usePartnerEnrollmentDialog(partnerId);
  const partnerConfigList = usePartnerConfigList();

  const getCompanyPartnerRelation = useCallback(
    (localPartnerId: string) => companyPartnerRelations?.find(
      (cp) => cp?.partnerId === localPartnerId,
    ),
    [companyPartnerRelations],
  );

  const subscriptionTier = company?.subscription?.subscriptionTier;
  const isPaidSubscription = !!subscriptionTier && subscriptionTier !== SubscriptionTier.EDGE;

  const companyPartnerRelation = getCompanyPartnerRelation(partnerId);
  const hdRelation = partnerId === homeDepotPartnerId ?
    companyPartnerRelation :
    undefined;
  const enrollmentStatus = getPartnerEnrollmentStatus(
    companyPartnerRelation,
  );
  const effectiveEnrollmentStatus = overrideEnrollmentStatus ?? enrollmentStatus;
  const currentPartnerListConfig = partnerConfigList.find((partner) => partner.id === partnerId);
  const dependencyShopPartners =
    currentPartnerListConfig?.manufacturerDistributors
      ?.map((distributor) => partnerConfigList.find(
        (partner) => partner.id === distributor.partnerId,
      ))
      .filter((partner): partner is NonNullable<typeof partner> => !!partner) ?? [];

  const redirectPath = isNativeAppExperience ?
    HOME_DEPOT_SETUP_OR_SKIP_ROUTE :
    `${HOME_DEPOT_SETUP_WEB}?returnPath=${PARTNERS_ROUTE_HOME_DEPOT}`;

  const [showHDEnrollModal, setShowHDEnrollModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);

  const submitHDEnrollmentEmail = async (email: string) => {
    setEmailLoading(true);
    try {
      const emailRequest: UpdateCompanyPartnerRequest = {
        partnerMemberEmail: email,
        enrollmentStatus: PartnerEnrollmentStatus.InProgress,
      };
      await dispatch(updateCompanyPartner({
        companyId: company?.id || '',
        partnerId: homeDepotPartnerId,
        data: emailRequest,
      })).then(unwrapResult);

      trackPartnerEnrollment('Submit', 'HomeDepotAccountEmail', {
        homeDepotEmail: email,
        isOptimizelyEvent: true,
      });
      trackPartnerEnrollment('Complete', 'HomeDepotSignup', {
        homeDepotEmail: email,
        isOptimizelyEvent: true,
      });
      trackPartnerEnrollment('Submit', 'Form', {
        partnerName: 'Home Depot',
        enrollmentType: 'Unknown',
        isOptimizelyEvent: true,
      });

      if (company?.id) {
        localStorage.removeItem(`hd_enrollment_step1_done_${memberId}`);
        localStorage.removeItem(`hd_enrollment_step2_done_${memberId}`);
        await dispatch(getCompanyPartnerRelations(company.id));
        setEmailLoading(false);
      } else {
        throw new Error('Company ID is missing');
      }
    } catch {
      setOpenErrorSnackbar(true);
      setEmailLoading(false);
    }
  };

  const enrollHomeDepotBadgeClicked = () => {
    trackHomeDepotEnrollmentStart();
    setShowHDEnrollModal(true);
  };

  // "Add rewards" on the partners list navigates here with a query param instead of
  // opening the dialog itself, so the two entry points land on the same modal (SN-924).
  useEffect(() => {
    if (
      companyPartnerRelations !== undefined &&
      partnerId === homeDepotPartnerId &&
      getHomeDepotEnrollmentStatus(hdRelation) === PartnerEnrollmentStatus.NotStarted &&
      shouldAutoOpenHomeDepotEnrollModal(location.search)
    ) {
      enrollHomeDepotBadgeClicked();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, hdRelation, companyPartnerRelations]);

  const enrollmentStatusMessageKey = getPartnerEnrollmentStatusMessageKey(
    currentPartnerListConfig?.partnerCode,
    effectiveEnrollmentStatus,
  );

  const renderShopActions = () => {
    if (effectiveEnrollmentStatus !== PartnerEnrollmentStatus.Completed) return null;

    // Paid tiers reach "completed" by expressing interest (SN-850) rather than by opening an
    // account with the partner, so a shop-and-earn CTA would send them to a program they are
    // not enrolled in yet. Product scoped these CTAs to Edge only.
    if (isPaidSubscription) return null;

    if (dependencyShopPartners.length > 0) {
      // Only show shop buttons for distributors where the member has a completed enrollment
      const enrolledShopPartners = dependencyShopPartners.filter((shopPartner) => {
        const relation = getCompanyPartnerRelation(shopPartner.id);
        return getPartnerEnrollmentStatus(relation) === PartnerEnrollmentStatus.Completed;
      });

      if (enrolledShopPartners.length === 0) return null;

      return (
        <Box sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: theme.spacing(1),
        }}>
          {enrolledShopPartners.map((shopPartner) => {
            const brandStyle = getPartnerShopButtonBrandStyle(shopPartner.partnerCode);
            return (
              <OptimusButton
                key={shopPartner.id}
                size="s"
                customVariant="primary"
                trailingIcon={<OpenInNewIcon />}
                onClick={shopPartner.openPartnerSite}
                buttonText={t('partnerDetails.enrollmentStatus.shopAtAndEarn', {
                  partnerName: shopPartner.title,
                  defaultValue: `Shop at ${shopPartner.title} and earn`,
                }) as string}
                sx={brandStyle ? {
                  backgroundColor: brandStyle.backgroundColor,
                  height: 'auto',
                  color: brandStyle.color,
                  fontWeight: 600,
                  '&:hover': { backgroundColor: brandStyle.backgroundColor },
                  '&:active': { backgroundColor: brandStyle.backgroundColor },
                } : { fontWeight: 600 }}
                data-testid={`${TestIds.partnerDetailsPage.shopNowBtnPrefix}-${shopPartner.id}`}
              />
            );
          })}
        </Box>
      );
    }

    if (!config?.primarySiteUrl) return null;

    const brandStyle = getPartnerShopButtonBrandStyle(currentPartnerListConfig?.partnerCode);

    return (
      <OptimusButton
        size="s"
        customVariant="primary"
        trailingIcon={<OpenInNewIcon />}
        onClick={() => openExternalLink(config.primarySiteUrl)}
        buttonText={t('partnerDetails.enrollmentStatus.shopAtAndEarn', {
          partnerName: introduction.title,
          defaultValue: `Shop at ${introduction.title} and earn`,
        }) as string}
        sx={brandStyle ? {
          backgroundColor: brandStyle.backgroundColor,
          height: 'auto',
          color: brandStyle.color,
          fontWeight: 600,
          '&:hover': { backgroundColor: brandStyle.backgroundColor },
          '&:active': { backgroundColor: brandStyle.backgroundColor },
        } : { fontWeight: 600 }}
        data-testid={TestIds.partnerDetailsPage.shopNowBtnPrefix}
      />
    );
  };

  const renderPartnerAccountNumber = () => {
    if (
      partnerId !== weSupplyTradesPartnerId ||
      effectiveEnrollmentStatus !== PartnerEnrollmentStatus.Completed ||
      !companyPartnerRelation?.partnerMemberKey
    ) {
      return null;
    }

    return (
      <Typography variant="body8" color={theme.palette.grey[700]}>
        {t('partnerDetails.enrollmentStatus.accountNumber', {
          accountNumber: companyPartnerRelation.partnerMemberKey,
        })}
      </Typography>
    );
  };

  const renderEnrollmentStatus = () => {
    const hasOverrideAction = overrideAction !== undefined;

    const isHDActionRequiredLoading = partnerId === homeDepotPartnerId &&
      getHomeDepotEnrollmentStatus(hdRelation) === PartnerEnrollmentStatus.NotStarted &&
      (updateMemberToPartnerState === 'pending' ||
        updateCompanyPartnerState === 'pending' ||
        getCompanyPartnerRelationsState === 'pending');

    if (isEnrollmentActionLoading || isHDActionRequiredLoading) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: theme.spacing(8),
        }}>
          <CircularProgress size={theme.spacing(3)} />
        </Box>
      );
    }

    if (partnerId === homeDepotPartnerId) {
      const hdStatus = overrideEnrollmentStatus ?? getHomeDepotEnrollmentStatus(hdRelation);
      if (hdStatus !== PartnerEnrollmentStatus.NotStarted) {
        return (
          <HDEnrollmentStatusPanel
            status={hdStatus}
            emailLoading={emailLoading}
            partnerMemberEmail={hdRelation?.partnerMemberEmail ?? undefined}
            onEmailSubmit={submitHDEnrollmentEmail}
            onShopNow={() => openExternalLink(HD_SITE_URL)}
            showShopAction={!isPaidSubscription}
          />
        );
      }
    }

    if (effectiveEnrollmentStatus === PartnerEnrollmentStatus.NotStarted) {
      if (hasOverrideAction) return overrideAction;

      return (
        <RenderEnrollmentBadge
          enrollStatus={effectiveEnrollmentStatus}
          updateOpenEnrollmentModalState={
            partnerId === homeDepotPartnerId ? enrollHomeDepotBadgeClicked : updatePartnerOpenState
          }
          partnerInfo={{ redirectPath }}
        />
      );
    }

    return (
      <>
        <PartnerEnrollmentStatusBadge status={effectiveEnrollmentStatus} />
        {enrollmentStatusMessageKey && !hasOverrideAction && (
          <Typography variant="body8" color={theme.palette.grey[700]}>
            {t(enrollmentStatusMessageKey)}
          </Typography>
        )}
        {hasOverrideAction && overrideAction}
        {renderShopActions()}
        {renderPartnerAccountNumber()}
      </>
    );
  };

  const CategoryBadge = ({ children }: { children: string }) => (<>
    <Box
      sx={{
        display: 'flex',
        padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
        borderRadius: theme.spacing(0.75),
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: '0 1px 2px 0 rgba(10, 13, 18, 0.05)',
      }}
    >
      <Typography variant='h10'>
        {children}
      </Typography>
    </Box>
  </>);

  return (
    <PartnerEnrollmentProvider>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Box
        data-testid={'partners-individual-overview'}
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            padding: theme.spacing(2),
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2),
            alignSelf: 'stretch',
            borderRadius: theme.spacing(2.5),
            background: theme.palette.brand.surface,
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'center',
                justifyContent: 'center',
                borderRadius: theme.spacing(2),
                background: theme.palette.common.white,
                border: `1.846px solid  ${theme.palette.grey[200]}`,
                boxShadow: '0 1.846px 3.692px 0 rgba(10, 13, 18, 0.05)',
                height: theme.spacing(12),
                width: theme.spacing(12),
              }}>
              <img
                src={config?.logoUrl}
                alt="Partner Logo"
                width={80}
                height={60}
                style={{
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Typography variant='h12' align='center'>{introduction.title}</Typography>
            {profileCategory?.map((category, index) => (
              <CategoryBadge key={index}>{category}</CategoryBadge>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            gap: theme.spacing(1.5),
            padding: theme.spacing(2),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.spacing(2.5),
            backgroundColor: theme.palette.common.white,
          }}
        >
          <Typography variant="body7">
            {effectiveEnrollmentStatus === PartnerEnrollmentStatus.NotStarted ?
              t('partnerDetails.enrollmentStatus.getStarted') :
              t('partnerDetails.enrollmentStatus.heading')}
          </Typography>
          {renderEnrollmentStatus()}
        </Box>
        {openPartnerModal && PartnerDialog}

        {/* HD Specific Enrollment flow */}
        <HDExistingAccModal
          open={showHDEnrollModal}
          onClose={() => setShowHDEnrollModal(false)}
          onSubmit={async (hasExistingAccount) => {
            if (hasExistingAccount) {
              localStorage.setItem(`hd_enrollment_step1_done_${memberId}`, 'true');
            }
            setShowHDEnrollModal(false);
            try {
              const actionRequiredRequest: UpdateCompanyPartnerRequest = {
                enrollmentStatus: PartnerEnrollmentStatus.ActionRequired,
              };
              await dispatch(updateCompanyPartner({
                companyId: company?.id || '',
                partnerId: homeDepotPartnerId,
                data: actionRequiredRequest,
              })).then(unwrapResult);
              if (company?.id) {
                await dispatch(getCompanyPartnerRelations(company.id));
              }
            } catch {
              setOpenErrorSnackbar(true);
            }
          }}
        />
      </Box>
    </PartnerEnrollmentProvider>
  );
};
